import { useEffect, useState } from 'react';
import { Loader2, Pencil, Save, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  useCreateOsmGeofence,
  useDeleteOsmGeofence,
  useOsmGeofences,
} from '@/api/hooks/useOsmGeofences';
import type { Geofence } from '@/types/osm';

export interface GeofenceDrawState {
  active: boolean;
  vertices: number[][];
}

interface GeofencePanelProps {
  drawState: GeofenceDrawState;
  setDrawState: (s: GeofenceDrawState) => void;
  pendingClick: { lng: number; lat: number } | null;
  consumePendingClick: () => void;
  onZoomTo: (g: Geofence) => void;
}

export default function GeofencePanel({
  drawState,
  setDrawState,
  pendingClick,
  consumePendingClick,
  onZoomTo,
}: GeofencePanelProps) {
  const list = useOsmGeofences();
  const create = useCreateOsmGeofence();
  const del = useDeleteOsmGeofence();
  const [name, setName] = useState('');
  const [kind, setKind] = useState('service_area');

  useEffect(() => {
    if (!pendingClick || !drawState.active) return;
    setDrawState({
      ...drawState,
      vertices: [...drawState.vertices, [pendingClick.lng, pendingClick.lat]],
    });
    consumePendingClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingClick]);

  function startDraw() {
    setDrawState({ active: true, vertices: [] });
  }
  function cancelDraw() {
    setDrawState({ active: false, vertices: [] });
  }
  function undo() {
    setDrawState({ ...drawState, vertices: drawState.vertices.slice(0, -1) });
  }

  async function save() {
    if (!name.trim() || drawState.vertices.length < 3) return;
    const ring = [...drawState.vertices, drawState.vertices[0]];
    try {
      await create.mutateAsync({
        name: name.trim(),
        kind: kind.trim() || 'service_area',
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
      setName('');
      cancelDraw();
    } catch {
      /* error rendered below */
    }
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">
      <div>
        <h3 className="text-sm font-semibold">Geofences</h3>
        <p className="text-xs text-muted-foreground">Click on the map to add polygon vertices.</p>
      </div>

      {!drawState.active ? (
        <Button type="button" size="sm" onClick={startDraw}>
          <Pencil className="mr-1 h-3.5 w-3.5" /> Draw new polygon
        </Button>
      ) : (
        <Card>
          <CardContent className="space-y-2 p-3 text-xs">
            <div className="font-medium">
              Drawing — {drawState.vertices.length} vertices
              {drawState.vertices.length < 3 && ' (need ≥3)'}
            </div>
            <div className="space-y-1">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Fence name"
                className="h-8 text-xs"
              />
              <Input
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                placeholder="Kind (service_area, no_go, alert…)"
                className="h-8 text-xs"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                size="sm"
                onClick={() => void save()}
                disabled={drawState.vertices.length < 3 || !name.trim() || create.isPending}
              >
                {create.isPending ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="mr-1 h-3.5 w-3.5" />
                )}
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={undo}
                disabled={!drawState.vertices.length}
              >
                Undo
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={cancelDraw}>
                <X className="mr-1 h-3.5 w-3.5" /> Cancel
              </Button>
            </div>
            {create.isError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-1 text-red-800">
                {(create.error as Error).message}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Saved fences
        </h4>
        {list.isLoading && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </div>
        )}
        {list.isError && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-2 text-xs text-red-800">
              {(list.error as Error).message}
            </CardContent>
          </Card>
        )}
        {list.data && list.data.length === 0 && (
          <p className="text-xs text-muted-foreground">No geofences saved yet.</p>
        )}
        <ul className="space-y-1">
          {list.data?.map((f) => (
            <li
              key={f.id}
              className="group flex items-center justify-between rounded-md border bg-card p-2 text-xs"
            >
              <button
                type="button"
                onClick={() => onZoomTo(f)}
                className="flex-1 text-left hover:underline"
              >
                <div className="font-medium">{f.name}</div>
                <div className="text-[10px] text-muted-foreground">
                  #{f.id} · {f.kind}
                </div>
              </button>
              <button
                type="button"
                title="Delete"
                onClick={() => {
                  if (confirm(`Delete "${f.name}"?`)) void del.mutate(f.id);
                }}
                className="text-muted-foreground hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
